import React from 'react';
import MagicButton from 'components/MagicButton/MagicButton';
import { FilterToken, SearchFilterState } from 'screens/Main/Search/searchState';
import { COLOR_PIPS } from './CardFilterTokenRow';
import { describeFilters, humanizeFilters } from './filterCopy';
import './FilteredEmptyState.css';

interface FilteredEmptyStateProps {
  /** "your want list", "this trader's have list", "this trader's trade history", "recent activity" */
  listNoun: string;
  /** What the data is counted in: "cards" (lists), "trades", or "events". */
  unitNoun?: string;
  unfilteredTotal: number;
  state: SearchFilterState;
  tokens: FilterToken[];
  lastAddedToken: FilterToken | undefined;
  onClearToken: (token: FilterToken) => void;
  onClearAll: () => void;
}

/**
 * Filtered-to-zero state (decision 12), per the approved mock: centered in the
 * empty body with generous vertical padding, a glyph row for the visual
 * filters (color pips / set), a heading, one prose sentence with an em dash,
 * chips tinted per filter, then "Clear <last filter>" (solid dark) beside
 * "Clear all filters" (purple). Rendered only when the list HAS data and the
 * filters removed all of it; the caller keeps its table header in place.
 */
export const FilteredEmptyState: React.FC<FilteredEmptyStateProps> = ({
  listNoun,
  unitNoun = 'cards',
  unfilteredTotal,
  state,
  tokens,
  lastAddedToken,
  onClearToken,
  onClearAll,
}) => {
  const filters = humanizeFilters(state, tokens);
  const glyphs = filters.filter((f) => f.colorKey || f.setLabel);
  const lastFilter = lastAddedToken ? filters.find((f) => f.id === lastAddedToken.id) ?? filters[filters.length - 1] : undefined;
  const heading =
    unitNoun === 'trades' ? 'No trades match these filters.'
      : unitNoun === 'events' ? 'No activity matches these filters.'
        : 'No cards match these filters.';
  const clear = (f: { clear: (s: SearchFilterState) => SearchFilterState; id: string }) =>
    onClearToken({ id: f.id, label: f.id, clear: f.clear });

  return (
    <div className="filtered-empty-state" data-testid="filtered-empty-state">
      {glyphs.length > 0 && (
        <div className="filtered-empty-glyphs" aria-hidden="true">
          {glyphs.map((g) =>
            g.colorKey ? (
              <img key={g.id} className="filtered-empty-glyph" src={COLOR_PIPS[g.colorKey].icon} alt="" />
            ) : (
              <span key={g.id} className="filtered-empty-glyph filtered-empty-glyph--set">{g.setLabel}</span>
            ),
          )}
        </div>
      )}
      <h3 className="filtered-empty-state-heading">{heading}</h3>
      {unfilteredTotal > 0 && (
        <p className="filtered-empty-state-body">
          All {unfilteredTotal.toLocaleString()} {unitNoun} in {listNoun} are still here — none of them{' '}
          {describeFilters(state, tokens)}.
        </p>
      )}
      <div className="filtered-empty-chips">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`filtered-empty-chip${f.colorKey ? ` filtered-empty-chip--${f.colorKey.toLowerCase()}` : ''}`}
            onClick={() => clear(f)}
            aria-label={`Remove filter ${f.chip}`}
          >
            {f.colorKey && <img src={COLOR_PIPS[f.colorKey].icon} alt="" aria-hidden="true" />}
            <span>{f.chip}</span>
            <span className="filtered-empty-chip-close" aria-hidden="true">×</span>
          </button>
        ))}
      </div>
      <div className="filtered-empty-state-actions">
        {lastFilter && (
          <MagicButton
            label={`Clear ${lastFilter.shortName}`}
            color="dark-grey"
            onPress={() => clear(lastFilter)}
            fullWidth={false}
          />
        )}
        <MagicButton label="Clear all filters" color="purple" onPress={onClearAll} fullWidth={false} />
      </div>
    </div>
  );
};

export default FilteredEmptyState;
